import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useMembershipListingAPI } from "@/app/hooks/api_hooks/Membership/useMembershipListingAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IMembershipState } from "@/app_redux/reducers/slices/auth/membership.slice";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import ModalForMembershipEdit from "./ModalForMembershipEdit";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { TbCircleLetterM } from "react-icons/tb";
import clsx from "clsx";
import { PRIMARY_BUTTON } from "@/constants";

const tableHeader = [
  { item: "Membership Name" },
  { item: "Category" },
  { item: "Created by & On" },
  { item: "Status" },
  { item: "Action" },
];

const MembershipSetup = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("MEMBERSHIP_SETUP", "id");
  const { routeState } = useRouteInfo(routeId);
  const { NO_APPOINTMENTS } = useImageConstants();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [membershipId, setMembershipId] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const isMembershipListInProgess = useLoader("isMembershipSetupListInProgress");
  const { membershipList } = routeState.state as IMembershipState;
  const { execute: fetchMemberList } = useMembershipListingAPI();

  useEffect(() => {
    fetchMemberList(searchQuery, "lising-page");
  }, [searchQuery]);

  const handelCouponEdit = (membership_id: string) => {
    setOpen(true);
    setMembershipId(membership_id);
  };

  const handelCouponCreation = () => {
    navigate("/app/membership-setup/membership-creation?page_step=general_details");
  };
  return (
    <div>
      <div className="px-4 sm:px-6 lg:px-8 mt-2 py-3 bg-white">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto mb-3">
            <h1 className="text-base font-medium leading-6 text-black">Memberships</h1>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="w-2/5">
            <InputWithAddOn
              onEnterKeyPress={(query: string) => setSearchQuery(query)}
              containerClassName="w-full"
              addon={<TbCircleLetterM className="w-4 h-4" />}
              placeholder="Search by Membership Name"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <button onClick={handelCouponCreation} className={`${PRIMARY_BUTTON} cursor-pointer`}>
            Setup Membership
          </button>
        </div>
      </div>
      <div className="px-4 sm:px-6 lg:px-8 mt-2 py-3 bg-white">
        <div className="mt-6 flow-root">
          {isMembershipListInProgess ? (
            <TableSkeleton />
          ) : membershipList?.length === 0 ? (
            <NoDataFoundComponent
              imageURL={NO_APPOINTMENTS}
              message="No Membership Available"
              description="No memberships have been available, please onboard some membership through create membership."
            />
          ) : (
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[600px]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="bg-white rounded-lg">
                      <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {tableHeader?.map((key, index) => (
                              <th
                                key={index}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {key.item}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {membershipList?.map((title, id) => {
                            const isFirst = id === 0;
                            const isLast = id === membershipList?.length - 1;
                            const isSingle = membershipList?.length === 1;

                            return (
                              <tr
                                key={id}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  {title?.name}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                  {title?.category}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                  {title?.created_by} {title?.created_at}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                  <div
                                    className={clsx(
                                      "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium",
                                      title?.status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700",
                                    )}
                                  >
                                    <span
                                      className={clsx(
                                        "w-2 h-2 rounded-full",
                                        title?.status ? "bg-green-500" : "bg-red-500",
                                      )}
                                    />
                                    {title?.status ? "Active" : "Inactive"}
                                  </div>
                                </td>
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tr-2xl rounded-br-2xl",
                                    isFirst && !isSingle && "rounded-tr-2xl",
                                    isLast && !isSingle && "rounded-br-2xl",
                                  )}
                                >
                                  {title?.membership_version === "v2" && (
                                    <a
                                      className="text-[#1F1F23] hover:bg-slate-200 bg-slate-100 inline-flex h-7 w-7 items-center justify-center rounded-xl transition cursor-pointer"
                                      onClick={() => handelCouponEdit(title?.membership_id)}
                                    >
                                      <PencilSquareIcon className="w-4 h-4" />
                                    </a>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {open && <ModalForMembershipEdit open={open} setOpen={setOpen} membershipId={membershipId} />}
    </div>
  );
};

export default MembershipSetup;
